/**
 * Dob3BoxInput — 3-box numeric date of birth input (MM / DD / YYYY).
 *
 * Layout and auto-advance behavior come from
 * `Primrose-Pymt-Portal-Homepage.html` / `Iris-Pymt-Portal-Homepage.html`:
 *   - Three numeric text fields: Month (2 digits), Day (2 digits), Year (4 digits)
 *   - Auto-advances focus MM -> DD -> YYYY once a box is full
 *   - Backspace on empty field returns focus to previous box
 *   - Emits normalized ISO date `YYYY-MM-DD` to the parent form
 *
 * Validation follows the previous portal's DOB field
 * (`trillium-payment-portal-ui`, `BaseComponents/Date.jsx` — a
 * `KeyboardDatePicker` with `onBlur={validateDob}`): the patient types
 * whatever they want without interference, and the date is checked when
 * they leave the field. An earlier attempt here refused out-of-range
 * keystrokes outright, which meant digits silently failing to appear as
 * the patient typed — the field fighting them instead of helping.
 *
 * So the only things touched while typing are the two the mockup already
 * specifies: non-digits are dropped, and each box stops at its `maxlength`.
 * Everything else — impossible months, Feb 30, future years — is reported
 * by `validateIsoDob` on blur, and again on submit by the parent form.
 */
import {
  useState,
  useRef,
  useEffect,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import { validateIsoDob } from '../../lib/dob';

interface DobParts {
  month: string;
  day: string;
  year: string;
}

interface Dob3BoxInputProps {
  /** ISO date string YYYY-MM-DD from parent form state. */
  value?: string;
  /** Emits ISO date string YYYY-MM-DD or raw string to parent. */
  onChange: (isoDate: string) => void;
  onBlur?: () => void;
  /**
   * Blur-time validation result, fired when the patient leaves the DOB row:
   * the message from `validateIsoDob`, or `null` when the date is fine.
   *
   * The field does not render this itself — the parent form already has one
   * error Alert, and a second copy under the boxes just says the same thing
   * twice.
   */
  onValidate?: (message: string | null) => void;
  theme: 'primrose' | 'iris';
  /** Set by the parent when validation failed, on blur or on submit. */
  error?: boolean;
}

/** Parse an ISO string (YYYY-MM-DD) into the three box values. */
function parseIso(iso: string): DobParts {
  const parts = iso.split('-');
  if (parts.length === 3) {
    return { year: parts[0] ?? '', month: parts[1] ?? '', day: parts[2] ?? '' };
  }
  return { month: '', day: '', year: '' };
}

export function Dob3BoxInput({
  value = '',
  onChange,
  onBlur,
  onValidate,
  theme,
  error,
}: Dob3BoxInputProps) {
  const [parts, setParts] = useState<DobParts>(() => parseIso(value));
  const { month, day, year } = parts;

  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Sync state if the external value changes (e.g. form reset).
  useEffect(() => {
    const parsed = parseIso(value);
    if (!value) {
      setParts({ month: '', day: '', year: '' });
    } else if (parsed.year && parsed.month && parsed.day) {
      setParts(parsed);
    }
  }, [value]);

  const emit = (next: DobParts) => {
    if (!next.month && !next.day && !next.year) {
      onChange('');
      return;
    }
    // Full-length parts join into a real ISO date; short ones deliberately
    // do not. Padding an empty month box to "00" here would have the
    // validator complain the month is out of range, when the real problem
    // is simply that the date is unfinished.
    onChange(`${next.year}-${next.month}-${next.day}`);
  };

  /**
   * Handle a keystroke in one box.
   *
   * `maxLength` is the only limit applied: digits are kept, the box stops
   * when full, and focus moves on. No value is ever rejected or rewritten.
   */
  const handleChange = (part: keyof DobParts, raw: string) => {
    const size = part === 'year' ? 4 : 2;
    const digits = raw.replace(/\D/g, '').slice(0, size);
    const next = { ...parts, [part]: digits };

    setParts(next);
    emit(next);

    if (digits.length === size) {
      if (part === 'month') dayRef.current?.focus();
      else if (part === 'day') yearRef.current?.focus();
    }
  };

  /**
   * Validate when the patient leaves the DOB row, exactly where the old
   * portal did it (`onBlur={validateDob}`).
   *
   * `relatedTarget` is the element gaining focus: moving between the three
   * boxes is still "inside" the field, so it must not trigger a complaint
   * about a date that is merely half-typed.
   */
  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;

    const empty = !month && !day && !year;
    onValidate?.(empty ? null : validateIsoDob(`${year}-${month}-${day}`));
    onBlur?.();
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    field: 'day' | 'year',
  ) => {
    if (e.key === 'Backspace') {
      if (field === 'day' && !day && monthRef.current) {
        monthRef.current.focus();
      } else if (field === 'year' && !year && dayRef.current) {
        dayRef.current.focus();
      }
    }
  };

  const inputClass = theme === 'iris' ? 'ir-input' : 'pr-input';
  const rowClass = theme === 'iris' ? 'ir-dob-row' : 'pr-dob-row';
  const errorBorder = error ? 'border-red-500' : '';

  return (
    <div className={rowClass} onBlur={handleBlur}>
      <input
        ref={monthRef}
        type="text"
        inputMode="numeric"
        placeholder="MM"
        aria-label="Month"
        maxLength={2}
        className={`${inputClass} ${errorBorder}`}
        value={month}
        onChange={(e) => handleChange('month', e.target.value)}
      />
      <input
        ref={dayRef}
        type="text"
        inputMode="numeric"
        placeholder="DD"
        aria-label="Day"
        maxLength={2}
        className={`${inputClass} ${errorBorder}`}
        value={day}
        onChange={(e) => handleChange('day', e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, 'day')}
      />
      <input
        ref={yearRef}
        type="text"
        inputMode="numeric"
        placeholder="YYYY"
        aria-label="Year"
        maxLength={4}
        className={`${inputClass} ${errorBorder}`}
        value={year}
        onChange={(e) => handleChange('year', e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, 'year')}
      />
    </div>
  );
}
